// SeriesInfoStore — IndexedDB local para detalhes enriquecidos de Série.
// Espelha vod-info-store: SWR (stale-while-revalidate) pra que 2ª abertura
// renderize IMEDIATO do cache local, com SSE atualizando em paralelo.
//
// Antes do split SSE, SeriesDetails bloqueava em fetch síncrono (Xtream
// + TMDB serial = 3-8s). Com IDB local + SSE, 1ª abertura mostra base em
// ~2s e enriched chega depois; 2ª abertura é instantânea.
//
// Schema versionado: bump SCHEMA_VERSION quando shape do payload mudar.
//
// **Scope idêntico ao local-db e vod-info-store**: chaves seguem
// `<providerPart>::<profilePart>::<seriesId>`. Versão 3 alinha com
// providerPart canônico (`p:CODE`/`d:LIST`) — antes era providerKey raw.

import type { Series } from '@app-iptv/iptv-client'
import type { DBSchema, IDBPDatabase } from 'idb'

import { makeDbOpener } from './idb-opener'
import { providerPart, profilePart } from './scope'

// v4 (2026-05-08): adiciona `lastReadAt` pra suportar LRU eviction
// no idb-quota-manager. Items v3 sem field tratados via fallback.
export const SERIES_INFO_SCHEMA_VERSION = 4

/** Episódio retornado pelo backend — shape do Xtream + enrichments TMDB. */
export interface SeriesEpisode {
  id: string
  episode_num?: string
  title?: string
  container_extension?: string | null
  season?: number
  info?: {
    plot?: string
    duration_secs?: number
    duration?: string
    movie_image?: string | null
    cover_big?: string | null
    rating?: string | null
    releasedate?: string
  }
}

export interface SeriesInfoPayload {
  series: Series
  seasons: Record<string, SeriesEpisode[]>
}

export interface SeriesInfoRecord {
  /** Chave composta: `${providerScope}::${profileScope}::${seriesId}` */
  key: string
  /** `p:CODE` ou `d:LIST`. Mesma forma do local-db. */
  providerScope: string
  /** `pf:<id>` ou `pf:_main`. Filtra por perfil sem escanear keys. */
  profileScope: string
  seriesId: string
  storedAt: number
  /** Última leitura — usado pelo LRU eviction do idb-quota-manager.
   *  Atualizado fire-and-forget no get(); custo trivial. */
  lastReadAt: number
  schemaVersion: number
  payload: SeriesInfoPayload
}

interface Schema extends DBSchema {
  seriesInfo: {
    key: string
    value: SeriesInfoRecord
    indexes: {
      'by-provider': string
      'by-profile': string
      'by-last-read': number // LRU index
    }
  }
}

const DB_NAME = 'iptv-series-info'
// v3: chave canônica (records v2 raw ficam órfãos).
// v4: adiciona índice by-last-read pra LRU eviction.
const DB_VERSION = 4

/**
 * Abertura via o molde unico (idb-opener.ts): solta a promise quando a
 * abertura falha, com guarda de identidade e janela de reabertura, e registra
 * o banco pro "Limpar dados". Antes, este modulo tinha o proprio `getDb`
 * com a promise da abertura guardada pra sempre: UMA rejeicao matava o cache
 * de fichas de serie pela sessao inteira, em silencio.
 *
 * Falha de abertura continua fluindo pro catch de cada metodo (→ rede).
 */
const seriesInfoDb = makeDbOpener<Schema>({
  name: DB_NAME,
  version: DB_VERSION,
  upgrade(db, oldVersion, _newVersion, tx) {
    if (oldVersion < 3 && db.objectStoreNames.contains('seriesInfo')) {
      db.deleteObjectStore('seriesInfo')
    }
    if (!db.objectStoreNames.contains('seriesInfo')) {
      const store = db.createObjectStore('seriesInfo', { keyPath: 'key' })
      store.createIndex('by-provider', 'providerScope')
      store.createIndex('by-profile', 'profileScope')
      store.createIndex('by-last-read', 'lastReadAt')
    } else if (oldVersion < 4) {
      // v3 → v4: store mantida; adiciona índice by-last-read.
      const store = tx.objectStore('seriesInfo')
      if (!store.indexNames.contains('by-last-read')) {
        store.createIndex('by-last-read', 'lastReadAt')
      }
    }
  },
})

function getDb(): Promise<IDBPDatabase<Schema>> {
  return seriesInfoDb.get()
}

/** TTL: 3 dias. Episódios novos podem aparecer em séries em produção;
 *  TTL menor que VOD (7d) cobre esse caso sem ficar cego. SWR atualiza
 *  via SSE em background sempre. */
export const SERIES_INFO_TTL_MS = 3 * 24 * 60 * 60 * 1000

/** Debounce do touch lastReadAt — só atualiza se passou >1min desde
 *  último touch. SWR pattern (cache + revalidate) faz get 2× rápido;
 *  sem debounce eram 2 puts redundantes. M2 do code review. */
const TOUCH_DEBOUNCE_MS = 60_000

function composeKey(
  seriesId: string,
): { key: string; providerScope: string; profileScope: string } | null {
  const provider = providerPart()
  if (!provider) return null
  const profile = profilePart()
  return {
    key: `${provider}::${profile}::${seriesId}`,
    providerScope: provider,
    profileScope: profile,
  }
}

export const seriesInfoStore = {
  /** Lê item do cache. Touch lastReadAt fire-and-forget pra LRU. */
  async get(seriesId: string): Promise<SeriesInfoRecord | null> {
    const composed = composeKey(seriesId)
    if (!composed) return null
    try {
      const db = await getDb()
      const record = await db.get('seriesInfo', composed.key)
      if (!record) return null
      if (record.schemaVersion !== SERIES_INFO_SCHEMA_VERSION) return null
      if (Date.now() - record.storedAt > SERIES_INFO_TTL_MS) {
        await db.delete('seriesInfo', composed.key)
        return null
      }
      // Touch lastReadAt fire-and-forget COM DEBOUNCE (M2 do review).
      // Records v3 órfãos (lastReadAt undefined) sempre touch.
      const now = Date.now()
      const lastRead = record.lastReadAt
      const shouldTouch = typeof lastRead !== 'number' || now - lastRead > TOUCH_DEBOUNCE_MS
      if (shouldTouch) {
        const touched: SeriesInfoRecord = { ...record, lastReadAt: now }
        void db.put('seriesInfo', touched).catch(() => undefined)
      }
      return record
    } catch {
      return null
    }
  },

  async set(seriesId: string, payload: SeriesInfoPayload): Promise<void> {
    const composed = composeKey(seriesId)
    if (!composed) return
    try {
      const db = await getDb()
      const now = Date.now()
      const record: SeriesInfoRecord = {
        key: composed.key,
        providerScope: composed.providerScope,
        profileScope: composed.profileScope,
        seriesId,
        storedAt: now,
        lastReadAt: now,
        schemaVersion: SERIES_INFO_SCHEMA_VERSION,
        payload,
      }
      await db.put('seriesInfo', record)
    } catch {
      // sem disco — segue sem cache
    }
  },

  /** Limpa todos os perfis de um parceiro. Reservado pra "Limpar dados
   *  deste parceiro" (granular vs hardResetLocalDb). Sem caller hoje. */
  async clearProvider(providerScope: string): Promise<void> {
    try {
      const db = await getDb()
      const tx = db.transaction('seriesInfo', 'readwrite')
      const idx = tx.store.index('by-provider')
      let cursor = await idx.openCursor(providerScope)
      while (cursor) {
        await cursor.delete()
        cursor = await cursor.continue()
      }
      await tx.done
    } catch {
      // ignora
    }
  },

  /** Remove items expirados ou de schema antigo. Roda em background
   *  pelo long-session-cleaner. Retorna quantos items removidos.
   *
   *  2-pass (M3 do code review): coleta keys readonly + bulk delete
   *  readwrite. Evita comportamento não-determinístico de cursor.delete
   *  durante iteração em IDB Tizen 2017. Cap MAX_PRUNE_PER_RUN = 1000. */
  async pruneExpired(): Promise<number> {
    const MAX_PRUNE_PER_RUN = 1000
    try {
      const db = await getDb()
      const now = Date.now()
      const expiredKeys: string[] = []
      let cursor = await db.transaction('seriesInfo', 'readonly').store.openCursor()
      while (cursor && expiredKeys.length < MAX_PRUNE_PER_RUN) {
        const r = cursor.value
        if (
          r.schemaVersion !== SERIES_INFO_SCHEMA_VERSION ||
          now - r.storedAt > SERIES_INFO_TTL_MS
        ) {
          expiredKeys.push(r.key)
        }
        cursor = await cursor.continue()
      }
      if (expiredKeys.length === 0) return 0
      const tx = db.transaction('seriesInfo', 'readwrite')
      await Promise.all(expiredKeys.map((k) => tx.store.delete(k)))
      await tx.done
      return expiredKeys.length
    } catch {
      return 0
    }
  },

  /** Conta items no store. Usado pelo idb-quota-manager pra decidir
   *  política de eviction. */
  async count(): Promise<number> {
    try {
      const db = await getDb()
      return await db.count('seriesInfo')
    } catch {
      return 0
    }
  },

  /** Evict LRU: remove até `targetRemoval` items mais antigos por
   *  `lastReadAt`. Retorna quantos removidos.
   *
   *  M3 do code review: 1 transação readonly engloba os 2 cursors
   *  (índice + scan pra órfãos), em vez de 2 transações separadas.
   *  Crítico em vlow Tizen 2017 — overhead de openTransaction × 2
   *  causava frame drop no player rodando junto. */
  async evictLru(targetRemoval: number): Promise<number> {
    if (targetRemoval <= 0) return 0
    try {
      const db = await getDb()
      const keysFromIndex: string[] = []
      const orphans: { key: string; storedAt: number }[] = []

      const readTx = db.transaction('seriesInfo', 'readonly')

      // Índice by-last-read ASC.
      let idxCursor = await readTx.store.index('by-last-read').openCursor()
      while (idxCursor && keysFromIndex.length < targetRemoval) {
        keysFromIndex.push(idxCursor.value.key)
        idxCursor = await idxCursor.continue()
      }

      const remaining = targetRemoval - keysFromIndex.length
      if (remaining > 0) {
        let scanCursor = await readTx.store.openCursor()
        while (scanCursor) {
          const r = scanCursor.value
          if (typeof r.lastReadAt !== 'number') {
            orphans.push({ key: r.key, storedAt: r.storedAt })
          }
          scanCursor = await scanCursor.continue()
        }
      }
      await readTx.done

      const keysToEvict = [...keysFromIndex]
      if (remaining > 0 && orphans.length > 0) {
        orphans.sort((a, b) => a.storedAt - b.storedAt)
        for (const o of orphans.slice(0, remaining)) {
          keysToEvict.push(o.key)
        }
      }
      if (keysToEvict.length === 0) return 0

      const writeTx = db.transaction('seriesInfo', 'readwrite')
      await Promise.all(keysToEvict.map((k) => writeTx.store.delete(k)))
      await writeTx.done
      return keysToEvict.length
    } catch {
      return 0
    }
  },

  /** Limpa cache de um perfil específico. Reservado — futura tela de
   *  gestão de perfis ("apagar dados deste perfil"). Sem caller hoje. */
  async clearProfile(profileScope: string): Promise<void> {
    try {
      const db = await getDb()
      const tx = db.transaction('seriesInfo', 'readwrite')
      const idx = tx.store.index('by-profile')
      let cursor = await idx.openCursor(profileScope)
      while (cursor) {
        await cursor.delete()
        cursor = await cursor.continue()
      }
      await tx.done
    } catch {
      // ignora
    }
  },
}
